import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@wms/shared';
import { apiFetch, ApiError } from '../../lib/api';
import { useElevatedFetch } from '../../lib/elevation';
import { useSession } from '../../lib/session';
import { useCurrentWarehouseId } from '../../lib/warehouse';
import { LocationInspector } from './location-inspector';
import { LocationTreeSelect } from './location-tree-select';
import { buildChildrenIndex, type LocationNode, type LocationTypeItem } from './types';

/**
 * 位置树页（Spec §6，架构书 §9）。M2 交付真实可展开的树 + 搜索 + 拖拽改父
 * 确认流程；虚拟化列表与画布留给 M3a/M11（见 M2 计划报告第 10 条范围记录）。
 */
export function LocationTreePage(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const elevatedFetch = useElevatedFetch();
  const warehouseId = useCurrentWarehouseId();

  const canManage = session?.permissions.includes(PERMISSIONS.WAREHOUSE_MANAGE) ?? false;
  // 【U-02】location.delete 默认仅 ADMIN——归档后的位置从这里删除
  const canDelete = session?.permissions.includes(PERMISSIONS.LOCATION_DELETE) ?? false;
  const [showArchived, setShowArchived] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LocationNode | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const {
    data,
    isPending,
    isError: locationsError,
  } = useQuery({
    queryKey: ['locations', warehouseId, showArchived],
    queryFn: () =>
      apiFetch<{ items: LocationNode[] }>(`/locations${showArchived ? '?includeArchived=true' : ''}`),
    enabled: !!warehouseId,
    // 访问被拒绝时 apiFetch 已经清掉了本地仓库选择（见 lib/api.ts），
    // <RequireWarehouse> 会据此把用户带回选择页——这里不需要重试。
    retry: false,
  });
  const { data: typesData } = useQuery({
    queryKey: ['location-types'],
    queryFn: () => apiFetch<{ items: LocationTypeItem[] }>('/location-types'),
  });

  const nodes = data?.items ?? [];
  const typeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const type of typesData?.items ?? []) map.set(type.id, t(type.nameKey, type.code));
    return map;
  }, [typesData, t]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createParentId, setCreateParentId] = useState<string | null | undefined>(undefined);
  const [newName, setNewName] = useState('');
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<{ id: string; newParentId: string | null } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const childrenIndex = useMemo(() => buildChildrenIndex(nodes), [nodes]);
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  const matchIds = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return null;
    return new Set(nodes.filter((n) => n.name.toLowerCase().includes(term)).map((n) => n.id));
  }, [nodes, search]);

  // 搜索命中时自动展开祖先，命中项才不会被折叠隐藏
  const effectiveExpanded = useMemo(() => {
    if (!matchIds) return expanded;
    const next = new Set(expanded);
    for (const id of matchIds) {
      let cursor = byId.get(id)?.parentId ?? null;
      while (cursor) {
        next.add(cursor);
        cursor = byId.get(cursor)?.parentId ?? null;
      }
    }
    return next;
  }, [matchIds, expanded, byId]);

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['locations', warehouseId] });
  };

  const createLocation = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    try {
      await elevatedFetch(PERMISSIONS.WAREHOUSE_MANAGE, '/locations', {
        parentId: createParentId ?? undefined,
        name: newName,
        canStoreInventory: true,
        sortOrder: 0,
      });
      setNewName('');
      setCreateParentId(undefined);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await apiFetch(`/locations/${deleteTarget.id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      if (selectedId === deleteTarget.id) setSelectedId(null);
      await queryClient.invalidateQueries({ queryKey: ['locations', warehouseId] });
    } catch (err) {
      setDeleteError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    }
  };

  const confirmMove = async (): Promise<void> => {
    if (!pendingMove) return;
    setError(null);
    try {
      await apiFetch(`/locations/${pendingMove.id}/move`, {
        method: 'POST',
        body: { newParentId: pendingMove.newParentId },
      });
      setPendingMove(null);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
      setPendingMove(null);
    }
  };

  function renderRows(parentId: string | null, depth: number): React.JSX.Element[] {
    const children = childrenIndex.get(parentId) ?? [];
    return children.flatMap((node) => {
      const hasChildren = (childrenIndex.get(node.id) ?? []).length > 0;
      const isOpen = effectiveExpanded.has(node.id);
      const dimmed = matchIds ? !matchIds.has(node.id) : false;

      const row = (
        <div
          key={node.id}
          className={`tree__row ${selectedId === node.id ? 'tree__row--selected' : ''} ${
            dragOverId === node.id ? 'tree__row--drop-target' : ''
          }`}
          style={{ paddingLeft: `${depth * 20}px`, opacity: dimmed ? 0.5 : 1 }}
          onClick={() => setSelectedId(node.id)}
          draggable={canManage}
          onDragStart={(e) => e.dataTransfer.setData('text/location-id', node.id)}
          onDragOver={(e) => {
            if (!canManage) return;
            e.preventDefault();
            setDragOverId(node.id);
          }}
          onDragLeave={() => setDragOverId((cur) => (cur === node.id ? null : cur))}
          onDrop={(e) => {
            e.preventDefault();
            setDragOverId(null);
            const draggedId = e.dataTransfer.getData('text/location-id');
            if (draggedId && draggedId !== node.id) {
              setPendingMove({ id: draggedId, newParentId: node.id });
            }
          }}
        >
          {hasChildren ? (
            <button
              type="button"
              className="tree__toggle"
              onClick={(e) => {
                e.stopPropagation();
                toggle(node.id);
              }}
            >
              {isOpen ? '▾' : '▸'}
            </button>
          ) : (
            <span className="tree__toggle tree__toggle--spacer" />
          )}
          <span
            className={
              node.canStoreInventory ? 'tree__label' : 'tree__label tree__label--non-storage'
            }
          >
            {node.name}
          </span>
          {node.typeId && <span className="tree__badge">{typeNameById.get(node.typeId)}</span>}
          {!node.canStoreInventory && (
            <span className="tree__badge" title={t('locations.notStorageHint')}>
              {t('locations.notStorageBadge')}
            </span>
          )}
          {node.status === 'ARCHIVED' && (
            <span className="badge badge--muted">{t('locations.archivedBadge')}</span>
          )}
          {node.status === 'ARCHIVED' && canDelete && (
            <button
              type="button"
              className="btn btn--danger btn--sm"
              style={{ marginLeft: 'auto' }}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteError(null);
                setDeleteTarget(node);
              }}
            >
              {t('locations.delete')}
            </button>
          )}
        </div>
      );

      return hasChildren && isOpen ? [row, ...renderRows(node.id, depth + 1)] : [row];
    });
  }

  if (!warehouseId) return <p>{t('shell.noWarehouse')}</p>;

  return (
    <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--space-6)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row row--between" style={{ alignItems: 'flex-start' }}>
          {/* M3a 起 /layout 是画布编辑器，这一页搬到 /locations，标题随之改成"位置树" */}
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.locations')}</h1>
          {/* 【用户设计】右上纵排：新建位置在上，"显示已归档位置"做成开关式
              按钮放它下面（开=实心/关=描边）；左侧搜索与树因此整体上移 */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
              alignItems: 'flex-end',
            }}
          >
            {canManage && (
              <button className="btn btn--primary btn--sm" onClick={() => setCreateParentId(null)}>
                {t('locations.createRoot')}
              </button>
            )}
            <button
              type="button"
              className={showArchived ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
              aria-pressed={showArchived}
              onClick={() => setShowArchived((s) => !s)}
            >
              {t('locations.showArchived')}
            </button>
          </div>
        </div>

        <input
          className="field"
          style={{ margin: 'var(--space-2) 0 var(--space-4)', maxWidth: '360px' }}
          placeholder={t('app.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {error && <p className="field-error">{error}</p>}
        {isPending && <p>{t('app.loading')}</p>}
        {/* 【M2 独立复审 LOW】仓库被归档/访问被收回时不要显示一个"看起来没有
            数据"的空状态——那会让用户以为这个仓库真的是空的。这里只是过渡态：
            apiFetch 已经清掉了失效的本地选择，<RequireWarehouse> 马上会把
            用户带回 S2 选择页。 */}
        {locationsError && (
          <div className="empty-state">
            <p>{t('locations.warehouseUnavailable')}</p>
          </div>
        )}
        {!isPending && !locationsError && nodes.length === 0 && (
          <div className="empty-state">
            <p>{t('empty.noResults')}</p>
          </div>
        )}

        <div className="tree">{renderRows(null, 0)}</div>
      </div>

      {selectedId && (
        <LocationInspector
          locationId={selectedId}
          canManage={canManage}
          onClose={() => setSelectedId(null)}
          onNavigate={setSelectedId}
          onCreateChild={(parentId) => setCreateParentId(parentId)}
          onChanged={() => void refresh()}
          onArchived={() => {
            setSelectedId(null);
            void refresh();
          }}
        />
      )}

      {createParentId !== undefined && (
        <div className="modal-overlay" onClick={() => setCreateParentId(undefined)}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void createLocation(e)}
          >
            <h2>{t('locations.createTitle')}</h2>
            <label className="field">
              <span>{t('locations.name')}</span>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
                autoFocus
              />
            </label>
            <label className="field">
              <span>{t('locations.parent')}</span>
              <LocationTreeSelect
                warehouseId={warehouseId}
                value={createParentId ?? null}
                onChange={setCreateParentId}
              />
            </label>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setCreateParentId(undefined)}
              >
                {t('app.cancel')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={!newName.trim()}>
                {t('app.save')}
              </button>
            </div>
          </form>
        </div>
      )}

      {pendingMove && (
        <div className="modal-overlay" onClick={() => setPendingMove(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('locations.confirmMoveTitle')}</h2>
            <p>
              {t('locations.confirmMoveBody', {
                name: byId.get(pendingMove.id)?.name ?? '',
                target: pendingMove.newParentId
                  ? (byId.get(pendingMove.newParentId)?.name ?? '')
                  : t('locations.warehouseRoot'),
              })}
            </p>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setPendingMove(null)}>
                {t('app.cancel')}
              </button>
              <button className="btn btn--primary" onClick={() => void confirmMove()}>
                {t('app.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 【U-02】删除已归档位置——仅 location.delete(默认 ADMIN)可见入口 */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('locations.deleteTitle', { name: deleteTarget.name })}</h2>
            <p>{t('locations.deleteBody')}</p>
            {deleteError && <p className="field-error">{deleteError}</p>}
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setDeleteTarget(null)}>
                {t('app.cancel')}
              </button>
              <button className="btn btn--danger" onClick={() => void confirmDelete()}>
                {t('locations.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
